import { useEffect, useState } from "react";
import {
  Tooltip,
  formatAbsolute,
  formatRelative as dsFormatRelative,
  useTimeZonePreference,
  type TimestampProps,
  type TimeZonePreference,
} from "@/components/ds";
import { cn } from "@/lib/cn";

// DLP's Timestamp: the ds/ one, except that a time slightly in the future
// reads "just now". Agent clocks run a little ahead of the server, so an event
// that just happened came out as "in 1 sec". ds/ is shared with other repos
// and must stay unchanged, so the override lives here; index.ts exports it in
// place of the ds/ Timestamp and formatRelative.

/** Future times closer than this are clock skew, not the future. */
export const CLOCK_SKEW_MS = 5 * 60 * 1000;

export function formatRelative(d: Date, now: number = Date.now()): string {
  const ahead = d.getTime() - now;
  if (ahead > 0 && ahead < CLOCK_SKEW_MS) return "just now";
  return dsFormatRelative(d, now);
}

/* One shared 30s tick for every relative timestamp on the page. */
const listeners = new Set<() => void>();
let ticker: ReturnType<typeof setInterval> | null = null;
function useTick(enabled: boolean): void {
  const [, force] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const fn = () => force((n) => n + 1);
    listeners.add(fn);
    if (!ticker) ticker = setInterval(() => listeners.forEach((l) => l()), 30_000);
    return () => {
      listeners.delete(fn);
      if (listeners.size === 0 && ticker) { clearInterval(ticker); ticker = null; }
    };
  }, [enabled]);
}

function toDate(value: TimestampProps["value"]): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function Timestamp({ value, format = "absolute", noSeconds, fallback = "—", className }: TimestampProps) {
  const [preference] = useTimeZonePreference();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const zone: TimeZonePreference = mounted ? preference : "utc";
  useTick(format !== "absolute");
  const d = toDate(value);
  if (!d) return <span className={cn("rc-timestamp rc-timestamp--empty", className)}>{fallback}</span>;

  const absolute = formatAbsolute(d, zone, !noSeconds);
  const relative = formatRelative(d);
  const other = formatAbsolute(d, zone === "utc" ? "local" : "utc");
  const text = !mounted ? absolute : format === "relative" ? relative : format === "both" ? `${absolute} · ${relative}` : absolute;
  const detail = format === "relative" ? `${absolute} (${other})` : `${relative} · ${other}`;

  return (
    <Tooltip content={detail}>
      <time dateTime={d.toISOString()} className={cn("rc-timestamp", className)}>
        {text}
      </time>
    </Tooltip>
  );
}
